import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Fact} from '@/components/fact'
import {BoolField, FieldGroup, NumberField, SelectField} from '@/components/wizard-fields'
import {
  strToBool,
  type Draft,
  type PerfilSociodemografico as PerfilRead,
  type PerfilSociodemograficoUpdate,
} from '@/hooks/empresa/mi_espacio/use-perfil-sociodemografico'
import {normalizeTallaCm} from '@/lib/validation'

const FRECUENCIA = ['nunca', 'ocasional', 'frecuente', 'diario'] as const

export function SaludStepFields({
  draft,
  perfil,
  godReadOnly,
  updateField,
  commit,
}: {
  draft: Draft
  perfil: PerfilRead
  godReadOnly: boolean
  updateField: (field: keyof Draft, value: string) => void
  commit: (fields: Partial<PerfilSociodemograficoUpdate>) => void
}) {
  const {t} = useTranslation()
  return (
    <>
      {/* Neutra y no `notice-amber`: el ámbar es estado pendiente (U2) y esta nota de custodia
          (T8) no pide acción. */}
      <p
        className="mb-4 rounded-lg border border-border bg-secondary px-3 py-2 text-xs text-muted-foreground"
        data-testid="perfil-sociodemografico-salud-privacy">
        {t('perfilSociodemografico.salud.privacyNote')}
      </p>
      <FieldGroup>
        <BoolField
          id="perfil-sd-enfermedad"
          label={t('perfilSociodemografico.enfermedadDiagnosticada')}
          value={draft.enfermedad_diagnosticada}
          disabled={godReadOnly}
          onChange={(v) => {
            updateField('enfermedad_diagnosticada', v)
            commit({enfermedad_diagnosticada: strToBool(v)})
          }}
        />
        {draft.enfermedad_diagnosticada === 'true' && (
          <div className="space-y-2">
            <Label htmlFor="perfil-sd-enfermedad-detalle">{t('perfilSociodemografico.enfermedadDetalle')}</Label>
            <Textarea
              id="perfil-sd-enfermedad-detalle"
              minRows={3}
              maxLength={2000}
              value={draft.enfermedad_detalle}
              disabled={godReadOnly}
              onChange={(e) => updateField('enfermedad_detalle', e.target.value)}
              onBlur={(e) => commit({enfermedad_detalle: e.target.value || null})}
            />
          </div>
        )}
        <BoolField
          id="perfil-sd-fuma"
          label={t('perfilSociodemografico.fuma')}
          value={draft.fuma}
          disabled={godReadOnly}
          onChange={(v) => {
            updateField('fuma', v)
            commit({fuma: strToBool(v)})
          }}
        />
        {draft.fuma === 'true' && (
          <SelectField
            id="perfil-sd-fuma-frecuencia"
            label={t('perfilSociodemografico.frecuenciaLabel')}
            value={draft.fuma_frecuencia}
            options={FRECUENCIA}
            optionLabel={(o) => t(`perfilSociodemografico.frecuencia.${o}`)}
            unspecifiedLabel={t('perfilSociodemografico.unspecified')}
            disabled={godReadOnly}
            onChange={(v) => {
              updateField('fuma_frecuencia', v)
              commit({fuma_frecuencia: (v || null) as PerfilSociodemograficoUpdate['fuma_frecuencia']})
            }}
          />
        )}
        <BoolField
          id="perfil-sd-alcohol"
          label={t('perfilSociodemografico.alcohol')}
          value={draft.alcohol}
          disabled={godReadOnly}
          onChange={(v) => {
            updateField('alcohol', v)
            commit({alcohol: strToBool(v)})
          }}
        />
        {draft.alcohol === 'true' && (
          <SelectField
            id="perfil-sd-alcohol-frecuencia"
            label={t('perfilSociodemografico.frecuenciaLabel')}
            value={draft.alcohol_frecuencia}
            options={FRECUENCIA}
            optionLabel={(o) => t(`perfilSociodemografico.frecuencia.${o}`)}
            unspecifiedLabel={t('perfilSociodemografico.unspecified')}
            disabled={godReadOnly}
            onChange={(v) => {
              updateField('alcohol_frecuencia', v)
              commit({alcohol_frecuencia: (v || null) as PerfilSociodemograficoUpdate['alcohol_frecuencia']})
            }}
          />
        )}
        <BoolField
          id="perfil-sd-sustancias"
          label={t('perfilSociodemografico.sustanciasPsicoactivas')}
          value={draft.sustancias_psicoactivas}
          disabled={godReadOnly}
          onChange={(v) => {
            updateField('sustancias_psicoactivas', v)
            commit({sustancias_psicoactivas: strToBool(v)})
          }}
        />
        <BoolField
          id="perfil-sd-ejercicio"
          label={t('perfilSociodemografico.ejercicio')}
          value={draft.ejercicio}
          disabled={godReadOnly}
          onChange={(v) => {
            updateField('ejercicio', v)
            commit({ejercicio: strToBool(v)})
          }}
        />
        {draft.ejercicio === 'true' && (
          <>
            <SelectField
              id="perfil-sd-ejercicio-frecuencia"
              label={t('perfilSociodemografico.frecuenciaLabel')}
              value={draft.ejercicio_frecuencia}
              options={FRECUENCIA}
              optionLabel={(o) => t(`perfilSociodemografico.frecuencia.${o}`)}
              unspecifiedLabel={t('perfilSociodemografico.unspecified')}
              disabled={godReadOnly}
              onChange={(v) => {
                updateField('ejercicio_frecuencia', v)
                commit({ejercicio_frecuencia: (v || null) as PerfilSociodemograficoUpdate['ejercicio_frecuencia']})
              }}
            />
            <div className="space-y-2">
              <Label htmlFor="perfil-sd-ejercicio-deporte">{t('perfilSociodemografico.ejercicioDeporte')}</Label>
              <Input
                id="perfil-sd-ejercicio-deporte"
                value={draft.ejercicio_deporte}
                maxLength={120}
                disabled={godReadOnly}
                onChange={(e) => updateField('ejercicio_deporte', e.target.value)}
                onBlur={(e) => commit({ejercicio_deporte: e.target.value || null})}
              />
            </div>
          </>
        )}
        <NumberField
          id="perfil-sd-talla"
          label={t('perfilSociodemografico.tallaCm')}
          value={draft.talla_cm}
          min={50}
          max={260}
          disabled={godReadOnly}
          normalize={normalizeTallaCm}
          onChange={(v) => updateField('talla_cm', v)}
          onCommit={(v) => commit({talla_cm: v})}
        />
        <NumberField
          id="perfil-sd-peso"
          label={t('perfilSociodemografico.pesoKg')}
          value={draft.peso_kg}
          min={20}
          max={400}
          disabled={godReadOnly}
          onChange={(v) => updateField('peso_kg', v)}
          onCommit={(v) => commit({peso_kg: v})}
        />
        {perfil.imc !== null && perfil.imc !== undefined && (
          // Sin disparador: el paso ya tiene el de su encabezado (≤ 1 por sección).
          <Fact label={t('perfilSociodemografico.imc')}>
            <span data-testid="perfil-sociodemografico-imc">{perfil.imc.toFixed(1)}</span>
          </Fact>
        )}
      </FieldGroup>
    </>
  )
}
